import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../lib/supabase';

// ── Month-to-date spend ────────────────────────────────────────────────

interface MTDSpend {
  total: number;
  dailyAverage: number;
  byProvider: { openai: number; assemblyai: number };
  callCount: { openai: number; assemblyai: number };
}

export function useMTDSpend() {
  return useQuery<MTDSpend>({
    queryKey: ['api-costs', 'mtd'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_mtd_api_spend');
      if (error) throw error;

      const openai = data?.find((r: any) => r.provider === 'openai');
      const assemblyai = data?.find((r: any) => r.provider === 'assemblyai');
      const total =
        (Number(openai?.total_cost) || 0) +
        (Number(assemblyai?.total_cost) || 0);

      // Calculate daily average based on days elapsed in current month
      const now = new Date();
      const daysElapsed = now.getDate();

      return {
        total,
        dailyAverage: daysElapsed > 0 ? total / daysElapsed : 0,
        byProvider: {
          openai: Number(openai?.total_cost) || 0,
          assemblyai: Number(assemblyai?.total_cost) || 0,
        },
        callCount: {
          openai: Number(openai?.call_count) || 0,
          assemblyai: Number(assemblyai?.call_count) || 0,
        },
      };
    },
    refetchInterval: 60_000, // Refresh every minute
  });
}

// ── Daily spend (last N days) ──────────────────────────────────────────

interface DailySpendRow {
  day: string;
  openai: number;
  assemblyai: number;
}

export function useDailySpend(daysBack = 30) {
  return useQuery<DailySpendRow[]>({
    queryKey: ['api-costs', 'daily', daysBack],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_daily_api_spend', {
        days_back: daysBack,
      });
      if (error) throw error;

      // Group by day, pivot providers into columns
      const dayMap = new Map<string, DailySpendRow>();
      for (const row of data || []) {
        const day = row.day;
        if (!dayMap.has(day)) {
          dayMap.set(day, { day, openai: 0, assemblyai: 0 });
        }
        const entry = dayMap.get(day)!;
        if (row.provider === 'openai') entry.openai = Number(row.total_cost) || 0;
        if (row.provider === 'assemblyai') entry.assemblyai = Number(row.total_cost) || 0;
      }

      return Array.from(dayMap.values()).sort((a, b) => a.day.localeCompare(b.day));
    },
    refetchInterval: 300_000, // Refresh every 5 minutes
  });
}

// ── Session cost table (paginated) ─────────────────────────────────────

interface SessionCostRow {
  id: string;
  created_at: string;
  provider: string;
  model: string | null;
  operation: string;
  tokens_in: number | null;
  tokens_out: number | null;
  audio_duration_seconds: number | null;
  estimated_cost_usd: number | null;
  session_id: string | null;
  org_id: string | null;
}

interface SessionCostFilters {
  provider?: 'openai' | 'assemblyai' | null;
}

export function useSessionCosts(
  page: number,
  pageSize: number,
  filters: SessionCostFilters = {},
) {
  return useQuery<{ rows: SessionCostRow[]; total: number }>({
    queryKey: ['api-costs', 'sessions', page, pageSize, filters.provider],
    queryFn: async () => {
      let query = supabase
        .from('api_usage_log')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      if (filters.provider) {
        query = query.eq('provider', filters.provider);
      }

      const { data, error, count } = await query;
      if (error) throw error;

      return { rows: data || [], total: count || 0 };
    },
    refetchInterval: 60_000,
  });
}
